/**
 * Sort-direction labels, chosen by the field's DATA TYPE (VERA-480).
 *
 * The bug this exists to kill: `apps/app` hardcoded "A - Z" / "Z - A" for every sortable
 * field, so sorting scans by DATE offered "A - Z" — meaningless for a timestamp, and it
 * hid which end of the sort was the recent one. The rule is that the labels follow the
 * column's type, never the position in a dropdown.
 *
 * This is the ONE map. `apps/platform`'s `Sort.tsx` consumed a private copy until
 * 2026-08-23 and now reads this (working-conventions §36/§47 — improve the base, do not
 * keep a sibling).
 *
 * ## The tuple is `[ASC, DESC]`, and an earlier draft of this file had it inverted
 *
 * That draft claimed to "mirror the platform exactly" and that "for dates the platform
 * maps ASC to 'Newest first'". Both claims were false, and the second one shipped a
 * user-visible lie in `apps/app`. What platform actually renders is
 * `<MenuItem value="asc">{labels[1]}</MenuItem>` — index ONE for ascending — over a
 * `date` tuple of `['Newest first', 'Oldest first']`, i.e. **ASC = "Oldest first"**.
 * So platform was correct and this file was the outlier.
 *
 * For `string`, `number`, `reference` and `multiSelect` the inversion was invisible,
 * because platform's array was simply this one reversed and its `[1]`/`[0]` indexing
 * reversed it back. `date`, `dateTime` and `boolean` were NOT symmetric, so `apps/app`
 * offered "Newest first" on the option that sorts OLDEST first: `compareValues`
 * (`apps/app/src/utils/certificateUtils.ts:81`) is a plain ascending comparator
 * (`a < b -> direction === 'ASC' ? -1 : 1`), as is `Provenance.tsx:126`
 * (`ASC ? ad - bd : bd - ad`). Ascending on a timestamp is oldest-first, always.
 *
 * `boolean` had the same shape: false (0) sorts before true (1), so ASC is "False".
 *
 * The three corrected tuples below are byte-identical to what platform renders today —
 * migrating it onto this map changes no platform pixel — and they fix the label in
 * `apps/app`, which is the app that had been reading them the declared way.
 */
export type SortFieldType =
  | 'string'
  | 'multiSelect'
  | 'number'
  | 'boolean'
  | 'date'
  | 'dateTime'
  | 'reference';

/** `[ascendingLabel, descendingLabel]` */
export type SortDirectionLabels = readonly [string, string];

const SORT_LABELS: Record<SortFieldType, SortDirectionLabels> = {
  string: ['A - Z', 'Z - A'],
  multiSelect: ['A - Z', 'Z - A'],
  number: ['Low - High', 'High - Low'],
  // false (0) < true (1), so ascending puts False first. Was ['True', 'False'].
  boolean: ['False', 'True'],
  // Ascending on a timestamp is OLDEST first. Was ['Newest first', 'Oldest first'],
  // which labelled the ascending option "Newest first" — see the note above.
  date: ['Oldest first', 'Newest first'],
  dateTime: ['Oldest first', 'Newest first'],
  // Reference fields resolve to a display name, so they read as text.
  reference: ['A - Z', 'Z - A'],
};

/** Text sort — the fallback for an unknown or undeclared type, matching the platform. */
const DEFAULT_SORT_LABELS: SortDirectionLabels = ['A - Z', 'Z - A'];

/**
 * Labels for a field's sort directions. Falls back to text labels when the type is
 * unknown, which is the same forgiving behaviour the platform has — a missing type
 * degrades to "A - Z" rather than crashing a sort menu.
 */
export const getSortDirectionLabels = (type?: SortFieldType | null): SortDirectionLabels =>
  (type && SORT_LABELS[type]) || DEFAULT_SORT_LABELS;
